import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import i18n from 'i18next';
import {memo, useMemo, useState} from 'react';
import {AiQueryCapabilityResult} from 'views/admin/adminAiQueryApi';
import {
  AiQueryDatasetSource,
  datasetRowCount,
  downloadDatasetCsv,
} from 'views/admin/aiQueryDatasetDownload';
import {ResultDataset} from 'views/admin/RecipeReplay';

import styles from './aiQueryConversation.module.css';
import {AiQueryInlineTableFooter} from './AiQueryInlineTableFooter';
import {
  INLINE_TABLE_VISIBLE_ROWS,
  inlineTableProjection,
  isTabularDataset,
} from './aiQueryInlineTables';

/**
 * A dataset table placed in an answer by a `[[table:<handle>]]` marker, drawn
 * from the structured rows (with person, workstream, and status cells) instead
 * of model-written Markdown. It previews the first rows (more on "Show more",
 * up to the preview the stream carried); the download holds every row.
 * Memoized so streamed text after it never re-renders its rows.
 */
export const AiQueryInlineTable = memo(
  ({
    dataset,
    isStreaming,
    source,
    onDownload,
    onDownloadError,
  }: {
    dataset: AiQueryCapabilityResult | undefined;
    isStreaming: boolean;
    source?: AiQueryDatasetSource;
    /** Called with the full row count when the download starts (for analytics). */
    onDownload?: (rowCount: number) => void;
    onDownloadError?: (error: unknown) => void;
  }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const projection = useMemo(() => dataset && inlineTableProjection(dataset), [dataset]);
    if (!dataset) {
      // Rows arrive before the model writes the marker, so this only shows for
      // a marker naming a dataset this turn never produced.
      return isStreaming ? (
        <Box paddingY="8">
          <Text size="small" color="faint">
            {i18n.t('ai_query_inline_table_loading')}
          </Text>
        </Box>
      ) : null;
    }
    if (dataset.items.length === 0 || !isTabularDataset(dataset)) {
      // Empty: the answer says so in prose. Non-tabular: Dash or doc search
      // results, which the answer cites as links.
      return null;
    }
    const total = datasetRowCount(dataset);
    const shown = isExpanded
      ? dataset.items.length
      : Math.min(dataset.items.length, INLINE_TABLE_VISIBLE_ROWS);
    return (
      <Box>
        <Box className={styles.inlineTable}>
          <ResultDataset
            result={dataset}
            projection={projection}
            previewRows={shown}
            showDownload={false}
            showCapNote={false}
          />
        </Box>
        {!isStreaming && total > INLINE_TABLE_VISIBLE_ROWS && (
          <AiQueryInlineTableFooter
            shown={shown}
            total={total}
            canExpand={dataset.items.length > INLINE_TABLE_VISIBLE_ROWS}
            isExpanded={isExpanded}
            onToggle={() => setIsExpanded((current) => !current)}
            onDownload={() => {
              onDownload?.(total);
              return downloadDatasetCsv(dataset, source);
            }}
            onDownloadError={onDownloadError}
          />
        )}
      </Box>
    );
  }
);
AiQueryInlineTable.displayName = 'AiQueryInlineTable';
